/**
 * D5 长度治理 · 阶段一/编辑期总高估算(设计稿 §4.7 第①道:控制区块数 + 预估总高,
 * 前端提示「当前约 X 屏」;第②道 maxHeight 15000×scale 硬钳在 ai orchestrator)。
 *
 * 口径:750 设计空间(与 ai bands._u 同基准)逐块启发式估高 —— 图块/A 类按画幅精确,
 * 文/数据块按 bands.py 版式常数近似(±15% 足够做屏数提示,精确高度由 ai 渲染时定)。
 */
import type { CanvasBlockForm } from "./canvasBridge";
import { STYLE_META } from "./blocks";

/** 750 设计空间一屏高(750×1334 手机屏口径)。 */
export const DESIGN_SCREEN_H = 1334;
/** ai orchestrator 同源:实际像素钳 = 15000 × scale(750 基准设计高)。 */
export const MAX_DESIGN_HEIGHT = 15000;

const RATIO_H: Record<string, number> = { "3:4": 4 / 3, "1:1": 1, "16:9": 9 / 16 };
/**
 * A 档资格:与 ai `orchestrator._effective_mode` 同口径 —— C3.1 起 A 放宽到**全部非图块
 * 样式**,只排除 qr(二维码要能扫,是功能红线)。
 *
 * ★改前这里还写着 D4 时代的「单段文案五件套」,数据块显式选 A 时估高会按 C 版式常数算
 * (几十行参数估成几百 px,ai 实际按 3:4 出图 = 1000px)—— 「约 X 屏」与硬钳提示直接说谎:
 * 页面说没超限,提交后被 ai 总高预检拒。
 */
const A_EXCLUDED_TAGS = new Set(["qr"]);
/** A 画幅梯子(由扁到高),与 ai `_A_RATIO_LADDER` 同源。 */
const A_RATIO_LADDER = ["16:9", "1:1", "3:4"] as const;

/**
 * A 档内容行数 —— 逐条镜像 ai `_a_content_spec`(text/items/series/headers/rows)。
 * 行数决定基准画幅,所以这里少数一行都会让屏数提示偏。
 */
function aContentLines(p: Record<string, unknown>): number {
  const lines: string[] = [];
  if ("text" in p) {
    lines.push(s(p.text));
    if (s(p.sub)) lines.push(s(p.sub));
    if (s(p.source)) lines.push(s(p.source));
    return lines.filter(Boolean).length;
  }
  for (const it of arr(p.items)) {
    if (it && typeof it === "object") {
      const o = it as Record<string, unknown>;
      const label = s(o.label).trim();
      const raw = o.value ?? o.score;
      const value = raw == null ? "" : String(raw).trim();
      lines.push(label && value ? `${label} —— ${value}` : label || value);
    } else {
      lines.push(String(it));
    }
  }
  for (const sr of arr(p.series)) {
    if (sr && typeof sr === "object") {
      const o = sr as Record<string, unknown>;
      lines.push(`${s(o.label)} —— ${o.value ?? ""}`);
    }
  }
  if (arr(p.headers).length) lines.push("表头");
  for (const row of arr(p.rows)) lines.push(Array.isArray(row) ? row.join(" | ") : String(row));
  return lines.filter(Boolean).length;
}

/** A 画幅:内容量定基准,主次(D6)提/降一格,两端钳死 —— 与 ai `_a_ratio_for` 同源。 */
function aRatioFor(nLines: number, emphasis: string | undefined): string {
  const base = nLines <= 3 ? 0 : nLines <= 7 ? 1 : 2;
  const shift = emphasis === "hero" ? 1 : emphasis === "minor" ? -1 : 0;
  return A_RATIO_LADDER[Math.max(0, Math.min(A_RATIO_LADDER.length - 1, base + shift))];
}
const TEXT_SIZE: Record<string, [size: number, charsPerLine: number]> = {
  bigtext: [42, 15],
  golden: [32, 20],
  body: [22, 30],
  cite: [24, 26],
  cta: [38, 17],
};

const s = (v: unknown) => (typeof v === "string" ? v : "");
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

/** 单块估高(750 设计空间 px)。 */
export function estimateBlockHeight(b: CanvasBlockForm): number {
  const meta = STYLE_META[b.styleTag];
  const p = b.props as Record<string, unknown>;
  if (meta?.primitive === "image") {
    return Math.round(750 * (RATIO_H[s(p.blockRatio)] ?? 4 / 3));
  }
  // A 类 = 整块交出图模型;画幅按内容行数 + 版面主次自适应(ai orchestrator 同源)
  if (b.canvas?.renderMode === "A" && !A_EXCLUDED_TAGS.has(b.styleTag)) {
    return Math.round(750 * RATIO_H[aRatioFor(aContentLines(p), b.canvas.emphasis)]);
  }
  const titleH = s(p.title) ? 34 : 0;
  const n = Math.max(1, arr(p.items).length || arr(p.series).length || arr(p.rows).length);
  switch (b.styleTag) {
    case "bigtext":
    case "golden":
    case "body":
    case "cite":
    case "cta": {
      const [size, perLine] = TEXT_SIZE[b.styleTag];
      const lines = Math.max(1, Math.ceil(s(p.text).length / perLine));
      const subH = s(p.sub) ? Math.ceil(s(p.sub).length / 38) * 24 + 10 : 0;
      const srcH = s(p.source) ? 30 : 0;
      return 88 + lines * (size + 10) + subH + srcH;
    }
    case "numlist":
      return 80 + titleH + n * 52;
    case "twocol":
      return 80 + titleH + Math.ceil(n / 2) * 52;
    case "stat":
    case "percent":
      return 80 + titleH + Math.ceil(n / 3) * 106;
    case "beforeafter":
      return 80 + titleH + Math.ceil(n / 2) * 106;
    case "comparebar":
      return 80 + titleH + n * 52;
    case "pie":
      return 80 + titleH + 236 + n * 30;
    case "line":
      return 80 + titleH + 264;
    case "table":
      return 80 + titleH + 44 * (n + 1);
    case "rating":
      return 80 + titleH + n * 50;
    case "qr":
      return 80 + 180 + (s(p.caption) ? 34 : 0);
    case "flow":
      return 80 + 34 + Math.ceil(n / 3) * 52;
    case "timeline":
      return 80 + 34 + n * 42;
    case "honor":
    case "media":
      return 80 + 34 + Math.ceil(n / 2) * 88;
    default:
      // pairs 家族默认行式(spec/infocard/price/rank/sales…);cards 布局按网格
      if (s(p.layout) === "cards") return 80 + 34 + Math.ceil(n / 2) * 88;
      return 80 + 34 + n * 46;
  }
}

export function estimateCanvasHeight(blocks: CanvasBlockForm[]): number {
  return blocks.reduce((sum, b) => sum + estimateBlockHeight(b), 0);
}

/** 屏数(750 空间比值,与 targetWidth/scale 无关 —— 分子分母同缩放)。 */
export function screensFor(designHeight: number): number {
  return designHeight / DESIGN_SCREEN_H;
}

/** targetWidth 下的设计空间硬钳(ai:实际像素 est ≤ 15000×scale;换算回 750 基准)。 */
export function maxDesignHeightFor(targetWidth: number): number {
  return Math.round((MAX_DESIGN_HEIGHT * 750) / Math.max(400, targetWidth));
}
